import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import { RotateCcw, RefreshCw, Users, Mail, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Admin: onboarding funnel (per-step completion) + refresher resets.
export default function OnboardingAnalyticsCard({ getAuthHeader }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [nudgeId, setNudgeId] = useState(null);
  const [nudgeBusy, setNudgeBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/onboarding/analytics`, { headers: getAuthHeader() });
      setData(res.data);
    } catch {
      toast.error('Could not load onboarding analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetUser = async (m) => {
    setBusyId(m.id);
    try {
      await axios.post(`${API_URL}/api/admin/onboarding/reset/${m.id}`, {}, { headers: getAuthHeader() });
      toast.success(`Onboarding reset for ${m.name || m.email} — they'll see the welcome modal on next visit`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    } finally {
      setBusyId(null);
    }
  };

  const resetInactive = async () => {
    setBulkBusy(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/onboarding/reset-inactive`,
        { days: 365 },
        { headers: getAuthHeader() },
      );
      toast.success(`Onboarding reset for ${res.data.reset} member${res.data.reset === 1 ? '' : 's'} inactive over a year`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk reset failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const nudgeUser = async (m) => {
    setNudgeId(m.id);
    try {
      await axios.post(`${API_URL}/api/admin/onboarding/nudge/${m.id}`, {}, { headers: getAuthHeader() });
      toast.success(`Nudge email sent to ${m.email}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Nudge failed');
    } finally {
      setNudgeId(null);
    }
  };

  const nudgeStalled = async () => {
    setNudgeBusy(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/onboarding/nudge-stalled`,
        { stall_days: 3 },
        { headers: getAuthHeader() },
      );
      const { sent, skipped_recent: skipped } = res.data;
      toast.success(`Nudge emails sent to ${sent} member${sent === 1 ? '' : 's'}${skipped ? ` (${skipped} skipped — nudged within the last week)` : ''}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk nudge failed');
    } finally {
      setNudgeBusy(false);
    }
  };

  return (
    <div className="wm-card p-6 rounded-lg border border-border space-y-5" data-testid="onboarding-analytics-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Onboarding Analytics</h3>
          <p className="text-sm text-muted-foreground mt-1">
            How far new members get through the welcome checklist — spot the drop-off step.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading} className="text-xs" data-testid="onboarding-analytics-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button disabled={nudgeBusy} variant="outline" className="text-xs" data-testid="nudge-stalled-btn">
                <Mail className="w-3.5 h-3.5 mr-1.5" />
                {nudgeBusy ? 'Sending…' : 'Email nudges'}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="nudge-stalled-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Nudge members who stalled?</AlertDialogTitle>
                <AlertDialogDescription>
                  Every member who started but hasn't finished the welcome checklist (account older
                  than 3 days) gets a friendly reminder email listing their remaining steps.
                  Members nudged within the last week are skipped automatically.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="nudge-stalled-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={nudgeStalled} data-testid="nudge-stalled-confirm">
                  Send nudge emails
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button disabled={bulkBusy} className="bg-amber-600 hover:bg-amber-700 text-white text-xs" data-testid="reset-inactive-btn">
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                {bulkBusy ? 'Resetting…' : 'Reset inactive 1+ year'}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="reset-inactive-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Reset onboarding for dormant members?</AlertDialogTitle>
                <AlertDialogDescription>
                  Every member whose last login (or signup, if never seen since) is over a year ago
                  gets a fresh welcome checklist — the modal shows again on their next visit as a refresher.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="reset-inactive-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={resetInactive} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="reset-inactive-confirm">
                  Reset dormant members
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {loading && !data ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !data ? null : (
        <>
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Eligible members', value: data.total_members },
              { label: 'Finished all steps', value: data.completed_all },
              { label: 'Skipped (incomplete)', value: data.dismissed_incomplete },
            ].map((s) => (
              <div key={s.label} className="rounded-lg bg-muted/40 border border-border p-3 text-center">
                <p className="text-2xl font-semibold text-foreground">{s.value}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>

          <div className="space-y-2.5" data-testid="onboarding-funnel">
            {data.steps.map((step) => (
              <div key={step.id}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-foreground">{step.label}</span>
                  <span className="text-muted-foreground">
                    {step.completed}/{step.applicable} · {step.pct}%
                  </span>
                </div>
                <Progress value={step.pct} className="h-2" />
              </div>
            ))}
          </div>

          {data.members.length > 0 && (
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="max-h-72 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 sticky top-0">
                    <tr className="text-left text-xs text-muted-foreground">
                      <th className="px-3 py-2">Member</th>
                      <th className="px-3 py-2">Progress</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.members.map((m) => (
                      <tr key={m.id} className="border-t border-border" data-testid={`onboarding-member-${m.id}`}>
                        <td className="px-3 py-2">
                          <p className="text-foreground text-xs font-medium">{m.name || '—'}</p>
                          <p className="text-[11px] text-muted-foreground">{m.email}</p>
                        </td>
                        <td className="px-3 py-2 text-xs text-foreground">
                          {m.completed_count}/{m.total_steps}
                        </td>
                        <td className="px-3 py-2">
                          {m.completed ? (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400">Done</span>
                          ) : m.dismissed ? (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400">Skipped</span>
                          ) : m.was_reset ? (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-500/15 text-indigo-400">Refresher</span>
                          ) : (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">In progress</span>
                          )}
                          {m.nudged_at && !m.completed && (
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              Nudged {new Date(m.nudged_at).toLocaleDateString()}
                            </p>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex gap-1.5">
                            {!m.completed && (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={nudgeId === m.id}
                                onClick={() => nudgeUser(m)}
                                className="text-xs h-7"
                                data-testid={`nudge-onboarding-${m.id}`}
                                title="Email a friendly reminder to finish setup"
                              >
                                <Send className="w-3 h-3" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={busyId === m.id}
                              onClick={() => resetUser(m)}
                              className="text-xs h-7"
                              data-testid={`reset-onboarding-${m.id}`}
                              title="Reset — they redo onboarding"
                            >
                              <RotateCcw className="w-3 h-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {data.members.length === 0 && (
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Users className="w-4 h-4" /> No onboarding-eligible members yet — new signups will appear here.
            </p>
          )}
        </>
      )}
    </div>
  );
}
